<script lang="ts" setup>
import type { IconsKeys } from '../icon/types'
import type { Color } from '../../types/shared'
import type {
  ButtonFontSize,
  ButtonRadius,
  ButtonSize,
  ButtonVariant,
  ButtonColor,
} from './types'

type Props = {
  variant?: ButtonVariant
  color?: ButtonColor
  size?: ButtonSize
  font?: ButtonFontSize
  radius?: ButtonRadius
  icon?: IconsKeys
  iconSize?: number | string
  iconColor?: Color
  loaderSize?: number | string
  disabled?: boolean
  loading?: boolean
  block?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  color: undefined,
  size: 'lg',
  font: 'lg',
  radius: 'md',
  icon: undefined,
  iconSize: 24,
  iconColor: 'currentColor',
  loaderSize: 24,
})

const slots = useSlots()
const attrs = useAttrs()

const component = computed(() => {
  if (attrs.href) return 'a'

  if (attrs.to) return resolveComponent('NuxtLink')

  return 'button'
})

const isOnlyIcon = computed(() => {
  return Boolean(props.icon) && !Object.keys(slots).length
})

const tabIndex = computed(() => {
  return props.disabled || props.loading ? -1 : 0
})

const componentClasses = computed(() => {
  return [
    'app-button',
    `app-button--variant-${props.variant}`,
    `app-button--size-${props.size}`,
    `app-button--font-${props.font}`,
    `app-button--radius-${props.radius}`,
    {
      [`app-button--color-${props.color}`]: props.color,
      'app-button--block': props.block,
      'app-button--disabled': props.disabled,
      'app-button--loading': props.loading,
      'app-button--only-icon': isOnlyIcon.value,
    },
  ]
})

const componentStyles = computed(() => {
  const style: Record<string, unknown> = {}

  if (attrs.width) style.width = `${attrs.width}px`

  if (attrs.height) style.height = `${attrs.height}px`

  if (attrs['background-color']) {
    style.backgroundColor = attrs['background-color']
  }

  return style
})
</script>

<template>
  <component
    :is="component"
    v-bind="$attrs"
    :class="componentClasses"
    :style="componentStyles"
    :tabindex="tabIndex"
    :disabled="disabled"
    :aria-busy="loading"
  >
    <the-loader v-if="loading" color="currentColor" :size="loaderSize" />

    <span class="app-button__content" :aria-hidden="loading">
      <the-icon v-if="icon" :name="icon" :size="iconSize" :color="iconColor" />

      <slot />

      <slot name="append" />
    </span>
  </component>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  font-weight: 800;
  color: inherit;
  text-decoration: none;
  text-transform: initial;
  cursor: pointer;
  user-select: none;
  border: none;
  transition: background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease,
    visibility 0.2s ease, border-color 0.2s ease;

  &:focus-visible {
    outline-width: 1px;
    outline-style: solid;
    outline-offset: 4px;
  }

  /* Elements */
  &__content {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    width: 100%;
    letter-spacing: 0.01em;
    white-space: nowrap;
    transition: opacity 0.2s ease;
  }

  /* States */
  &--block {
    width: 100%;
  }

  &--disabled {
    pointer-events: none;
  }

  &--loading {
    pointer-events: none;

    .app-button__content {
      opacity: 0;
    }
  }

  &--only-icon {
    padding: 0 !important;
  }

  /* Variant */
  &--variant-primary {
    color: var(--ui-color-white);
    background-color: var(--ui-color-coral-500);
    outline-color: var(--ui-color-coral-700);

    &:active {
      background-color: var(--ui-color-coral-700);
    }

    &:disabled {
      background-color: var(--ui-color-coral-300);
    }

    @include media('>=desktop-mini') {
      &:hover:not(:active) {
        background-color: var(--ui-color-coral-600);
      }
    }
  }

  &--variant-secondary {
    color: var(--ui-color-white);
    background-color: var(--ui-color-sandy-800);
    outline-color: var(--ui-color-sandy-800);

    &:active {
      background-color: var(--ui-color-sandy-950);
    }

    &:disabled {
      background-color: var(--ui-color-sandy-300);
    }

    @include media('>=desktop-mini') {
      &:hover:not(:active) {
        background-color: var(--ui-color-sandy-900);
      }
    }
  }

  &--variant-tertiary {
    color: var(--ui-text-primary);
    background-color: var(--ui-color-sandy-50);
    outline-color: var(--ui-color-sandy-950);

    &:active {
      background-color: var(--ui-color-sandy-200);
    }

    &:disabled {
      color: var(--ui-color-sandy-400);
      background-color: var(--ui-color-sandy-50);
    }

    @include media('>=desktop-mini') {
      &:hover:not(:active) {
        background-color: var(--ui-color-sandy-100);
      }
    }
  }

  &--variant-quaternary {
    color: var(--ui-color-coral-500);
    background-color: var(--ui-color-coral-50);
    outline-color: var(--ui-color-coral-700);

    &:active {
      background-color: var(--ui-color-coral-200);
    }

    &:disabled {
      color: var(--ui-color-coral-300);
      background-color: var(--ui-color-coral-50);
    }

    @include media('>=desktop-mini') {
      &:hover:not(:active) {
        background-color: var(--ui-color-coral-100);
      }
    }
  }

  &--variant-white {
    color: var(--ui-color-sandy-800);
    background-color: var(--ui-color-white);
    outline-color: var(--ui-color-sandy-800);

    &:active {
      color: var(--ui-color-sandy-950);
    }

    &:disabled {
      color: var(--ui-color-sandy-400);
    }

    @include media('>=desktop-mini') {
      &:hover:not(:active) {
        color: var(--ui-color-sandy-900);
      }
    }
  }

  &--variant-transparent {
    background-color: transparent;
  }

  /* Color. Only for transparent variant */
  &--color-primary {
    color: var(--ui-color-coral-500);
    outline-color: var(--ui-color-coral-700);
    outline-offset: 3px;

    &.app-button--size-xxs {
      outline-width: 2px;
      outline-offset: 2px;
    }

    &:active {
      color: var(--ui-color-coral-700);
    }

    &:disabled {
      color: var(--ui-color-coral-300);
    }

    &:focus-visible:not(.app-button--size-xxs) {
      box-shadow: 0 0 0 2px var(--ui-color-coral-500) inset;
    }

    @include media('>=desktop-mini') {
      &:hover:not(:active) {
        color: var(--ui-color-coral-600);
      }
    }
  }

  &--color-secondary {
    color: var(--ui-color-sandy-800);
    outline-color: var(--ui-color-sandy-800);
    outline-offset: 3px;

    &.app-button--size-xxs {
      outline-width: 2px;
      outline-offset: 2px;
    }

    &:active {
      color: var(--ui-color-sandy-950);
    }

    &:disabled {
      color: var(--ui-color-sandy-400);
    }

    &:focus-visible:not(.app-button--size-xxs) {
      box-shadow: 0 0 0 2px var(--ui-color-sandy-800) inset;
    }

    @include media('>=desktop-mini') {
      &:hover:not(:active) {
        color: var(--ui-color-sandy-900);
      }
    }
  }

  /* Size */
  &--size-xxs {
    min-width: 20px;
    min-height: 20px;
  }

  &--size-sm {
    min-width: 40px;
    min-height: 40px;
    padding: 0 12px;
  }

  &--size-lg {
    min-width: 56px;
    min-height: 56px;
    padding: 0 32px;
  }

  /* Font Size */
  &--font-sm {
    font-size: 12px;
    line-height: 16px;
  }

  &--font-md {
    font-size: 14px;
    line-height: 18px;
  }

  &--font-lg {
    font-size: 16px;
    line-height: 20px;
  }

  /* Radius */
  &--radius-sm {
    border-radius: 12px;
  }

  &--radius-md {
    border-radius: 16px;
  }

  &--radius-lg {
    border-radius: 120px;
  }
}
</style>
